import { ReactNode } from 'react';
import { css } from '@bts-web/utils-style-engine';
import { useClientTranslation } from '@bts-web/utils-lokalise';
import { TFunction } from 'i18next';
import { ButtonBase, DialogBase, HyperLinkButtonBase } from '../../common';
import { getSavingsPlansTranslations } from '../utils/getSavingsPlansTranslations';
import { SavingsPlansListTranslationsKeys } from '../types';
import { getAppConfig } from '@bts-web/core-app-config';
import { ModalComponent } from '../../common/components/ModalDrawerContext/ModalDrawerContext';
import {
  RegularDataTrending,
  RegularLightbulb,
  RegularSettings,
} from '../../common/svgIcons';

const container = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 'small',
  height: '100%',
});

const modalContainerStyles = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 'small',
  pb: 'small',
  height: '100%',
});

const modalSectionStyles = css({
  display: 'flex',
  gap: 'small',
});

const modalSectionTextStyles = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: 'extra_small_2',

  '& > h3': {
    color: 'neutrals.text_primary',
    fontSize: 'title.medium_medium',
    fontWeight: 'title.medium_medium',
    lineHeight: 'title.medium_medium',
    letterSpacing: 'title.medium_medium',
  },

  '& > span': {
    color: 'neutrals.text_secondary',
    fontSize: 'body.medium',
    fontWeight: 'body.medium',
    lineHeight: 'body.medium',
    letterSpacing: 'body.medium',
    textAlign: 'start',
  },
});

const buttonsContainer = css({
  marginTop: 'auto',
  display: 'flex',
  flexDirection: {
    base: 'column',
    md: 'row',
  },
  gap: 'extra_small',
  flexWrap: 'wrap',
  width: '100%',
  maxWidth: '100%',
  mx: 'auto',
});

export interface StartSavingModalProps {
  continueUrl: string;
}

interface SectionData {
  icon: ReactNode;
  iconPrefix?: string;
  theme?: string;
  titleKey: string;
  descriptionKey: string;
}

const sections: SectionData[] = [
  {
    icon: <RegularSettings size={24} />,
    iconPrefix: 'mdi',
    titleKey: 'startSavingSectionTitleOne',
    descriptionKey: 'startSavingSectionDescriptionOne',
  },
  {
    icon: <RegularLightbulb size={24} />,
    theme: 'regular',
    titleKey: 'startSavingSectionTitleTwo',
    descriptionKey: 'startSavingSectionDescriptionTwo',
  },
  {
    icon: <RegularDataTrending size={24} />,
    theme: 'regular',
    titleKey: 'startSavingSectionTitleThree',
    descriptionKey: 'startSavingSectionDescriptionThree',
  },
];

const StartSavingModal: ModalComponent<StartSavingModalProps> = ({
  onClose,
  continueUrl,
}) => {
  const { t } = useClientTranslation();

  const translations = getSavingsPlansTranslations(t as TFunction);

  const { appId } = getAppConfig();

  return (
    <DialogBase
      title={translations.startSavingTitle}
      id="start-saving-plans"
      visual="secondary"
      size="small"
      onClose={onClose}
      showHeaderBottomDivider
    >
      <div className={container}>
        <div className={modalContainerStyles}>
          {sections.map((section, index) => (
            <section key={index} className={modalSectionStyles}>
              {section.icon}
              <div className={modalSectionTextStyles}>
                <h3>
                  {
                    translations[
                      section.titleKey as unknown as keyof SavingsPlansListTranslationsKeys
                    ]
                  }
                </h3>
                <span>
                  {
                    translations[
                      section.descriptionKey as unknown as keyof SavingsPlansListTranslationsKeys
                    ]
                  }
                </span>
              </div>
            </section>
          ))}
        </div>

        <div className={buttonsContainer}>
          <HyperLinkButtonBase
            className={css({ flex: 1, maxWidth: '100%' })}
            visual={appId === 4 ? 'accent' : 'primary'}
            to={continueUrl}
            onClick={onClose}
          >
            {translations.continue}
          </HyperLinkButtonBase>

          <ButtonBase
            className={css({
              flex: 1,
              maxWidth: '100%',
            })}
            visual="secondary"
            onClick={onClose}
            size="large"
            fullWidth
          >
            {translations.goBack}
          </ButtonBase>
        </div>
      </div>
    </DialogBase>
  );
};

export { StartSavingModal };
